/**
 * The one numeric input.
 *
 * A `type="number"` field is the wrong tool for money: it accepts `1e-7` and
 * `-5`, its spinners nudge a size by a whole unit, a scroll over it changes the
 * value, and what it reports for a locale comma is empty string. This is a text
 * field with a decimal keypad that sanitises as the trader types, normalises a
 * paste, and resolves onto the market's grid when they leave — never before,
 * because reformatting mid-entry moves the caret.
 */
import { forwardRef } from "react";
import { finalizeValue, normalizePaste, sanitizeTyping } from "../../../utils/amount/sanitize";
import { NumericFieldSpec } from "../../../utils/amount/spec";
import { BareInput, InputShell, SlotLabel } from "./style";

export interface NumericInputProps {
  value: string;
  spec: NumericFieldSpec;
  onValueChange: (text: string) => void;
  /** Called on blur / Enter with the value resolved onto the grid. */
  onFinalize?: (text: string) => void;
  /** Label inside the field ("Order size"). */
  label?: string;
  /** Unit or unit picker, on the right ("BTC", a dropdown). */
  unitSlot?: React.ReactNode;
  /** Anything else on the right: a Mid chip, a MAX button. */
  rightSlot?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Id of the error text, so a screen reader reads it with the field. */
  describedBy?: string;
  ariaLabel?: string;
  inputId?: string;
  onBlurCapture?: () => void;
  /**
   * Pull an over-ceiling value down to `spec.max` on blur (the default). Off
   * where an out-of-range value is an error to show, not a value to replace —
   * slippage: a typed 60% silently becoming 50% is a bound nobody chose.
   */
  clampOnFinalize?: boolean;
}

export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
  (
    {
      value,
      spec,
      onValueChange,
      onFinalize,
      label,
      unitSlot,
      rightSlot,
      placeholder,
      disabled,
      invalid,
      describedBy,
      ariaLabel,
      inputId,
      onBlurCapture,
      clampOnFinalize = true,
    },
    ref
  ) => {
    const finalize = (text: string) => {
      const resolved = finalizeValue(
        text,
        clampOnFinalize ? spec : { ...spec, max: undefined }
      );
      if (resolved !== text) onValueChange(resolved);
      onFinalize?.(resolved);
    };

    return (
      <InputShell $invalid={invalid} $disabled={disabled}>
        {label ? <SlotLabel>{label}</SlotLabel> : null}
        <BareInput
          ref={ref}
          id={inputId}
          // Text, not number: see the note at the top of this file.
          type="text"
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          value={value}
          placeholder={placeholder ?? "0"}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          // `value` is what the field held: a keystroke that would change the
          // number's meaning ("1e3", "-1", "1.2.3") is rejected, not spliced.
          onChange={(event) =>
            onValueChange(
              sanitizeTyping(event.target.value, spec.maxDecimals, value)
            )
          }
          onPaste={(event) => {
            const pasted = event.clipboardData.getData("text");
            const normalized = normalizePaste(pasted, spec.maxDecimals);
            // Only intercept what the plain path would mangle ("1,234.56").
            if (normalized !== null) {
              event.preventDefault();
              onValueChange(normalized);
            }
          }}
          onBlur={(event) => {
            finalize(event.target.value);
            onBlurCapture?.();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") finalize(event.currentTarget.value);
            // Arrow keys would nudge a size by a whole unit on some browsers.
            if (event.key === "ArrowUp" || event.key === "ArrowDown") {
              event.preventDefault();
            }
          }}
          // A scroll over a focused field must not change what is being traded.
          onWheel={(event) => event.currentTarget.blur()}
        />
        {rightSlot}
        {unitSlot}
      </InputShell>
    );
  }
);

NumericInput.displayName = "NumericInput";
